/**
 * @module app.core
 */

import { autoinject, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { UserService } from 'app/core/services/user-service';
import { AccountService } from 'app/core/services/account-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { modelIdMatcher } from 'app/shared/lib/matcher';

@autoinject()
export class UserRoles extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    @bindable.object()
    protected user: Core.User | null = null;

    @bindable.boolean()
    protected isUserSynched: boolean = false;

    protected userRoles: Core.UserRole[] = [];
    protected userContext: Core.UserContext | null = null;
    protected savedSelectedUserRoles: Core.UserRole[] = [];
    protected homePage: Core.AccountHomePage | null = null;

    protected modelMatcher = modelIdMatcher;

    @computedFrom('userContext.roles.length', 'savedSelectedUserRoles.length')
    get hasChanges(): boolean {
        return this.userContext?.roles?.length !== this.savedSelectedUserRoles.length ||
            this.userContext?.roles?.some(role1 => !this.savedSelectedUserRoles.some(role2 => this.modelMatcher(role1, role2)));
    }

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly userService: UserService,
        private readonly accountService: AccountService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////
    protected bind(): void {
        this.loadUserRoles();
        this.loadUserContext();
        this.loadHomePageMyAccount();
    }


    ////****************** Functions ************************************************////
    protected buildUserContext(): Core.UserContext {
        return {
            id: this.userContext?.id,
            user: { id: this.user?.id },
            roles: this.userContext?.roles?.map(role => ({ id: role.id })),
            // we only need to set the home page and application when creating a new user context
            ...(!this.userContext?.id && {
                homePage: { id: this.homePage?.id },
                application: { code: "Suite" }
            })
        };
    }

    // Reverse any changes present on the User Roles form
    protected cancel() {
        if(this.userContext) {
            this.userContext.roles = deepClone(this.savedSelectedUserRoles);
        }
    }

    protected async submit(): Promise<void> {
        await this.saveUserRoles();
        await this.loadUserContext(); // reload selected user roles after updating
    }

    ////****************** Service Calls ********************************************////
    @apiCall // Load in all available roles
    private async loadUserRoles(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        const includes: string[] = [

        ];
        const fields: string[] = [
            "description"
        ];

        try {
            this.state.beginSave();
            const response = await this.userService.loadUserRolesByApplication(options, includes, fields);
            // Filter out the All Authenticated Users role
            this.userRoles = response.results.filter(r => r.description !== "All Authenticated Users");
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall // Load User Context along with currently selected roles
    private async loadUserContext(): Promise<void> {
        if(!this.user?.id) return;

        // We need to include inactive in case user context is deactivated
        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true
        };

        const includes: string[] = [
            "roles",
            "application"
        ];

        const fields: string[] = [
            "roles",
            "roles.description",
            "application.code"
        ];

        try {
            this.state.beginSave();
            const response = await this.userService.loadUserContext(this.user.id, options, includes, fields);
            this.userContext = response.results.filter(c => c.application?.code === "Suite")[0];
            if(!this.userContext) {
                this.userContext = { roles: [] };
            }
            this.savedSelectedUserRoles = deepClone(this.userContext?.roles || []);
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall // Save selected user roles by updating User Context
    private async saveUserRoles(): Promise<void> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            if(this.userContext?.roles?.length === 0) { // if no roles are selected
                if(this.userContext?.isActive) {
                    isSuccessful = await this.activateUserContext(); // deactivate user context, deactivating removes all roles from the user context
                }
            } else {
                if(!this.userContext?.id || !this.userContext?.isActive) {
                    isSuccessful = await this.activateUserContext(); // create or activate user context if it doesn't exist or is inactive
                }
                if(this.userContext?.id) {
                    const response = await this.userService.saveUserContext(this.buildUserContext());
                    isSuccessful = response.isSuccessful;
                }
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.updated('core:userRole.$long', isSuccessful, this.userContext?.roles?.length);
            await this.loadUserContext();
            this.state.endSave();
        }
    }

    private async loadHomePageMyAccount(): Promise<void> {
        try {
            this.state.beginLoad();

            const homePageMyAccount = await this.accountService.loadHomePageMyAccount();
            if(homePageMyAccount.isSuccessful) {
                this.homePage = homePageMyAccount.results[0];
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateUserContext(): Promise<boolean> {
        let isSuccessful = false;
        try {
            this.state.beginSave();
            // If no user context, then create it, else activate/deactivate it
            if(!this.userContext?.id) {
                const response = await this.userService.saveUserContext(this.buildUserContext());
                if(response.isSuccessful && this.userContext) {
                    this.userContext.id = response.id || null;
                }
                isSuccessful = response.isSuccessful;
            } else {
                const isActive = this.userContext.isActive || false;
                const response = await this.userService.activateUserContext(this.userContext.id, !isActive);
                isSuccessful = response.isSuccessful;
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }
}
